import { z } from 'zod';
import { NotificationKind } from '../enums';

/**
 * Notification shared schemas. Mirrors Prisma `Notification` + `NotificationKind`.
 *
 * Payload is `{ eventoId?, candidaturaId?, vagaId?, motivo?, ... }` — kept loose
 * (record of unknown) because each kind ships a distinct shape; consumers cast.
 */

export const NotificationKindSchema = z.enum([
  'CANDIDATURA_CRIADA',
  'CANDIDATURA_APROVADA',
  'CANDIDATURA_REJEITADA',
  'CANDIDATURA_CANCELADA',
  // Phase 10 v1.1 — distinct kinds for CANC-01 (profissional → empresa) and
  // CANC-03 (empresa → profissional) cancel notifications.
  'CANDIDATURA_CANCELADA_PROFISSIONAL',
  'CANDIDATURA_CANCELADA_EMPRESA',
  'EVENTO_PROXIMO',
  'EVENTO_FINALIZADO',
  'EQUIPE_CONVITE',
  'CARTEIRA_CREDITO',
  // GENERICO retained for backwards compat with historical rows; new
  // dispatches use one of the Phase 39 dedicated kinds.
  'GENERICO',
  // Phase 26 — CONVOCAR + bilateral rating reminder.
  'CONVOCACAO_RECEBIDA',
  'CONVOCACAO_CANCELADA',
  'AVALIACAO_PENDENTE',
  // Phase 33 — SUP-01. Founder reply to a support ticket.
  'SUPORTE_RESPOSTA',
  // Phase 39 v1.9 — KYC-SPLIT-01. Dedicated kinds replacing GENERICO.
  'KYC_REJEITADA',
  'SISTEMA_AVISO',
  'PIX_EXPIROU',
]);

export const NotificationDtoSchema = z.object({
  id: z.string(),
  kind: NotificationKindSchema,
  titulo: z.string(),
  corpo: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  lidaEm: z.string().nullable(),
  criadaEm: z.string(),
});

export const NotificationListResponseSchema = z.object({
  notifications: z.array(NotificationDtoSchema),
  total: z.number().int(),
  naoLidas: z.number().int(),
});

export const ContadorResponseSchema = z.object({
  naoLidas: z.number().int().nonnegative(),
});

// ---------- Inferred types ----------
// NotificationKind type lives in ../enums (canonical). Schema inference is identical;
// consumers import the value+type from package root.

export type NotificationDto = z.infer<typeof NotificationDtoSchema>;
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;
export type ContadorResponse = z.infer<typeof ContadorResponseSchema>;

// ============================================================
// PHASE 20 v1.4 — NOTIF-01: per-kind push/email preferences
// ============================================================

/**
 * Per-kind notification toggle. Both channels optional — missing channel
 * defaults to TRUE on dispatch (`prefs[kind]?.push ?? true`,
 * `prefs[kind]?.email ?? true`).
 */
export const NotificationChannelPrefsSchema = z.object({
  push: z.boolean().optional(),
  email: z.boolean().optional(),
});
export type NotificationChannelPrefs = z.infer<
  typeof NotificationChannelPrefsSchema
>;

/**
 * Full preference map stored on User.notificationPrefs. Sparse — most users
 * keep all kinds opted-in (empty object). Server stores whatever object the
 * client PUTs; the dispatcher reads `prefs[kind]?.channel ?? true` so missing
 * keys default to opt-in.
 *
 * Validation note: NotificationKind values are also accepted via the enum
 * schema below — z.record key validation is intentionally loose to forward-
 * compatibility with new kinds added without schema bump (server applies the
 * stricter key validation in the update endpoint).
 */
export const NotificationPrefsSchema = z.record(
  z.string(),
  NotificationChannelPrefsSchema,
);
export type NotificationPrefs = z.infer<typeof NotificationPrefsSchema>;

/**
 * PUT /me/notification-prefs body. One kind at a time — matches the most
 * common UX (toggle a single switch in settings). Server merges into existing
 * prefs preserving other kinds. `push` + `email` BOTH optional but at least
 * one MUST be present (refined check) — otherwise the call is a no-op.
 */
export const UpdateNotificationPrefInputSchema = z
  .object({
    kind: NotificationKindSchema,
    push: z.boolean().optional(),
    email: z.boolean().optional(),
  })
  .refine((v) => v.push !== undefined || v.email !== undefined, {
    message: 'Informe ao menos um canal (push ou email).',
  });
export type UpdateNotificationPrefInput = z.infer<
  typeof UpdateNotificationPrefInputSchema
>;

/** GET /me/notification-prefs response wrapper. */
export const NotificationPrefsResponseSchema = z.object({
  prefs: NotificationPrefsSchema,
});
export type NotificationPrefsResponse = z.infer<
  typeof NotificationPrefsResponseSchema
>;

// ============================================================
// PHASE 40 v1.9 — NOTIF-COARSE-01: 3-preset coarse mode
// ============================================================

/**
 * Coarse preset applied via `PUT /me/notification-prefs/coarse`. One of:
 *  - `all`       — enable push + email for every non-transactional kind in
 *                  the user's audience (transactional kinds untouched).
 *  - `essential` — enable only the high-signal kinds; mute everything else
 *                  in the user's audience. See `ESSENTIAL_NOTIFICATION_KINDS`
 *                  below for membership.
 *  - `none`      — disable push + email for every non-transactional kind in
 *                  the user's audience. Transactional kinds (CARTEIRA_CREDITO
 *                  / SUPORTE_RESPOSTA / KYC_REJEITADA) STAY enabled — the
 *                  dispatcher's hardcoded override is the final line of
 *                  defence anyway, so writing `false` here would only trigger
 *                  the Phase 36 422 guard.
 */
export const CoarsePresetSchema = z.enum(['all', 'essential', 'none']);
export type CoarsePreset = z.infer<typeof CoarsePresetSchema>;

export const ApplyCoarsePresetInputSchema = z.object({
  preset: CoarsePresetSchema,
});
export type ApplyCoarsePresetInput = z.infer<
  typeof ApplyCoarsePresetInputSchema
>;

/**
 * Non-transactional kinds that the `essential` preset keeps enabled. These
 * are the high-signal user-actionable notifications that, when muted, would
 * leave the profissional unaware that work was assigned or rejected.
 *
 * Transactional kinds (CARTEIRA_CREDITO / SUPORTE_RESPOSTA / KYC_REJEITADA)
 * are ALWAYS enabled regardless of preset — the dispatcher overrides them.
 * They are NOT listed here because the coarse preset code skips writing
 * them entirely (writing `false` would trigger the Phase 36 422 guard;
 * writing `true` would be a redundant no-op).
 *
 * REQUIREMENTS.md / NOTIF-COARSE-01 lists CARTEIRA_DEBITO as essential —
 * that kind does not exist in NotificationKind (CARTEIRA_CREDITO is the
 * only wallet kind today and it's transactional/always-on). Membership
 * here is the actionable non-transactional subset.
 */
export const ESSENTIAL_NOTIFICATION_KINDS = new Set<NotificationKind>([
  NotificationKind.CANDIDATURA_APROVADA,
  NotificationKind.CANDIDATURA_REJEITADA,
]) as ReadonlySet<NotificationKind>;

// ============================================================
// PHASE 36 v1.8 — NOTIF-PREF-04/05: defence-in-depth allowlist
// ============================================================

/**
 * Defence-in-depth allowlist for transactional notification kinds. Enforced
 * at 3 layers:
 *   1. UI (informational; Phase 37) — disables toggle + shows "obrigatório"
 *      badge with LGPD tooltip.
 *   2. UsersService.updateNotificationPref (422) — rejects programmatic
 *      attempts to set `push:false` / `email:false`.
 *   3. NotificationsDispatcher.dispatch (hardcoded override) — last line of
 *      defence: even if a stale or hand-crafted preference row says off,
 *      transactional dispatch ALWAYS goes through.
 *
 * Adding a kind here REMOVES user agency — start tight. Initial membership:
 *   - SUPORTE_RESPOSTA: silenciar founder reply quebra o ticket loop.
 *   - CARTEIRA_CREDITO: money movement = LGPD/fraud signal mandatory.
 *   - KYC_REJEITADA (Phase 39 KYC-SPLIT-01): identity-verification decision —
 *     user must be informed of rejection so they can resubmit; silencing this
 *     row would orphan the rejected profissional with no actionable feedback.
 */
export const TRANSACTIONAL_NOTIFICATION_KINDS = new Set<NotificationKind>([
  NotificationKind.SUPORTE_RESPOSTA,
  NotificationKind.CARTEIRA_CREDITO,
  NotificationKind.KYC_REJEITADA,
]) as ReadonlySet<NotificationKind>;

/**
 * Helper: is the given kind in `TRANSACTIONAL_NOTIFICATION_KINDS`? Use this
 * everywhere — never inline `.has(kind)` so the policy lives in ONE place.
 */
export function isTransactionalKind(kind: NotificationKind): boolean {
  return TRANSACTIONAL_NOTIFICATION_KINDS.has(kind);
}

/**
 * Map kind → audience(s). Drives UI role-filtering in Phase 37: profissionais
 * never see EMPRESA-only kinds, and vice-versa. Exhaustive switch — adding a
 * new NotificationKind enum value without a branch is a compile error.
 *
 * Returns `readonly ('profissional' | 'empresa')[]` so callers can iterate
 * without taking ownership of the array (the same tuple is reused across
 * calls).
 */
export function kindAudience(
  kind: NotificationKind,
): readonly ('profissional' | 'empresa')[] {
  switch (kind) {
    // Profissional inbox kinds — candidatura lifecycle from prof side.
    case NotificationKind.CANDIDATURA_APROVADA:
    case NotificationKind.CANDIDATURA_REJEITADA:
    case NotificationKind.CANDIDATURA_CANCELADA:
    case NotificationKind.CANDIDATURA_CANCELADA_EMPRESA:
    // Profissional gets evento lifecycle reminders + invites + ratings.
    case NotificationKind.EVENTO_PROXIMO:
    case NotificationKind.EVENTO_FINALIZADO:
    case NotificationKind.EQUIPE_CONVITE:
    case NotificationKind.CONVOCACAO_RECEBIDA:
    case NotificationKind.CONVOCACAO_CANCELADA:
    case NotificationKind.AVALIACAO_PENDENTE:
    // Carteira (wallet) is profissional-side — credit movements land in
    // the profissional's wallet, never the empresa's.
    case NotificationKind.CARTEIRA_CREDITO:
      return ['profissional'];
    // Empresa inbox kinds — new candidatura arrives + prof cancels.
    case NotificationKind.CANDIDATURA_CRIADA:
    case NotificationKind.CANDIDATURA_CANCELADA_PROFISSIONAL:
      return ['empresa'];
    // GENERICO + SUPORTE_RESPOSTA + SISTEMA_AVISO can land in either inbox
    // depending on who the User is (profissional or empresa membro). Both
    // audiences receive these kinds.
    case NotificationKind.GENERICO:
    case NotificationKind.SUPORTE_RESPOSTA:
    case NotificationKind.SISTEMA_AVISO:
      return ['profissional', 'empresa'];
    // Phase 39 KYC-SPLIT-01 — KYC decisions target the profissional whose
    // documents were reviewed; empresa never receives KYC rows.
    case NotificationKind.KYC_REJEITADA:
    // PIX_EXPIROU goes to the wallet owner — currently always profissional
    // (WalletTopup); empresa PIX expiration uses a different channel (event
    // republish flow) so this kind is profissional-only.
    case NotificationKind.PIX_EXPIROU:
      return ['profissional'];
  }
}

// ============================================================
// PHASE 44 v1.10 — DIGEST-01: weekly email digest opt-in
// ============================================================

/**
 * PUT /me/notification-prefs/email-digest body. Single boolean — toggle the
 * weekly email digest. Default OFF in DB (LGPD): user must explicitly opt in.
 *
 * NOT a NotificationKind: digest is a separate user preference (cron-driven
 * batch email of last 7 days), so it lives on its own boolean column
 * (`User.emailDigestOptIn`) rather than as a key inside `notificationPrefs`.
 */
export const SetEmailDigestOptInInputSchema = z.object({
  optIn: z.boolean(),
});
export type SetEmailDigestOptInInput = z.infer<
  typeof SetEmailDigestOptInInputSchema
>;

/**
 * GET /me/notification-prefs/email-digest response. The mobile settings page
 * pulls this at load time to render the switch initial state.
 */
export const EmailDigestPrefResponseSchema = z.object({
  optIn: z.boolean(),
});
export type EmailDigestPrefResponse = z.infer<
  typeof EmailDigestPrefResponseSchema
>;

/**
 * UI display metadata per kind. `category` drives the grouping in the Phase
 * 37 settings page (4 sections: Candidaturas, Eventos, Carteira & Pagamentos,
 * Conta & Suporte). `label` is the toggle row title; `description` is the
 * tooltip / helper line shown under it.
 *
 * The `satisfies` guard FORCES exhaustiveness: adding a new `NotificationKind`
 * enum entry without a matching key here is a TypeScript compile error — the
 * UI never silently drops a kind.
 */
export type KindCategory =
  | 'candidaturas'
  | 'eventos'
  | 'carteira_pagamentos'
  | 'conta_suporte';

export type KindMeta = {
  label: string;
  description: string;
  category: KindCategory;
};

export const KIND_LABELS = {
  CANDIDATURA_CRIADA: {
    label: 'Nova candidatura recebida',
    description: 'Avisa quando um profissional se candidata a uma das suas vagas.',
    category: 'candidaturas',
  },
  CANDIDATURA_APROVADA: {
    label: 'Candidatura aprovada',
    description: 'Avisa quando você é aprovado para uma vaga.',
    category: 'candidaturas',
  },
  CANDIDATURA_REJEITADA: {
    label: 'Candidatura não aprovada',
    description: 'Avisa quando sua candidatura não é aprovada.',
    category: 'candidaturas',
  },
  CANDIDATURA_CANCELADA: {
    label: 'Candidatura cancelada',
    description: 'Avisa sobre cancelamentos genéricos de candidatura.',
    category: 'candidaturas',
  },
  CANDIDATURA_CANCELADA_PROFISSIONAL: {
    label: 'Profissional cancelou candidatura',
    description: 'Avisa quando um profissional cancela uma candidatura na sua vaga.',
    category: 'candidaturas',
  },
  CANDIDATURA_CANCELADA_EMPRESA: {
    label: 'Empresa cancelou o evento',
    description: 'Avisa quando o evento da sua candidatura é cancelado pela empresa.',
    category: 'candidaturas',
  },
  EVENTO_PROXIMO: {
    label: 'Lembrete de evento próximo',
    description: 'Avisa 24 a 48 horas antes do início do evento confirmado.',
    category: 'eventos',
  },
  EVENTO_FINALIZADO: {
    label: 'Evento finalizado',
    description: 'Avisa quando um evento que você participou é finalizado.',
    category: 'eventos',
  },
  EQUIPE_CONVITE: {
    label: 'Convite para equipe',
    description: 'Avisa quando você recebe um convite direto para uma equipe.',
    category: 'eventos',
  },
  CONVOCACAO_RECEBIDA: {
    label: 'Nova convocação',
    description: 'Avisa quando uma empresa convoca você diretamente para uma vaga.',
    category: 'eventos',
  },
  CONVOCACAO_CANCELADA: {
    label: 'Convocação cancelada',
    description: 'Avisa quando uma empresa cancela uma convocação direta enviada a você.',
    category: 'eventos',
  },
  AVALIACAO_PENDENTE: {
    label: 'Avaliação pendente',
    description: 'Lembra de avaliar a outra parte após um evento finalizado.',
    category: 'eventos',
  },
  CARTEIRA_CREDITO: {
    label: 'Crédito na carteira',
    description:
      'Avisa quando um pagamento é creditado na sua carteira. Notificação obrigatória — exigida por lei ou contrato.',
    category: 'carteira_pagamentos',
  },
  GENERICO: {
    label: 'Avisos da plataforma',
    description: 'Comunicados gerais e informativos da Lance-X.',
    category: 'conta_suporte',
  },
  SUPORTE_RESPOSTA: {
    label: 'Resposta do suporte',
    description:
      'Avisa quando o time de suporte responde um ticket aberto por você. Notificação obrigatória — exigida por lei ou contrato.',
    category: 'conta_suporte',
  },
  // Phase 39 v1.9 — KYC-SPLIT-01. Dedicated kinds split from GENERICO.
  KYC_REJEITADA: {
    label: 'Verificação de identidade não aprovada',
    description:
      'Avisa quando sua verificação de identidade (KYC) é rejeitada, incluindo o motivo informado pela equipe. Notificação obrigatória — exigida por lei ou contrato.',
    category: 'conta_suporte',
  },
  SISTEMA_AVISO: {
    label: 'Avisos da plataforma',
    description:
      'Comunicados gerais, novidades e atualizações de status enviados pela Lance-X.',
    category: 'conta_suporte',
  },
  PIX_EXPIROU: {
    label: 'PIX expirado',
    description:
      'Avisa quando um pagamento PIX gerado por você expira antes da confirmação. Gere um novo QR Code para continuar.',
    category: 'carteira_pagamentos',
  },
} as const satisfies Record<NotificationKind, KindMeta>;

// ============================================================
// PHASE 45 v1.10 — SNOOZE-01: quiet hours window
// ============================================================

/**
 * 24h HH:mm format regex. Matches `00:00` … `23:59` inclusive. Used by both
 * the backend setter (validation pipe) and the mobile form (Reactive Form
 * validator) so the message stays in sync.
 *
 * Rejects: '7:00', '07:0', '24:00', '23:60', '7:00 AM', '7am'.
 * Accepts: '00:00', '07:30', '22:15', '23:59'.
 */
export const QUIET_HOURS_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const QuietHoursTimeSchema = z
  .string()
  .regex(QUIET_HOURS_TIME_REGEX, {
    message: 'Horário deve estar no formato HH:mm (00:00..23:59).',
  });

/**
 * Quiet hours value object — `{ start, end }` both HH:mm OR `null` when
 * the user has cleared the window. Server represents the same state as
 * two nullable columns (`User.quietHoursStart` + `User.quietHoursEnd`); both
 * null ≡ disabled.
 *
 * Wrap-around windows are LEGAL (e.g. `start='22:00'` + `end='07:00'`) —
 * the dispatcher's in-window check handles spans across midnight.
 * `start === end` is rejected at the setter (degenerate empty window).
 */
export const QuietHoursSchema = z.object({
  start: QuietHoursTimeSchema,
  end: QuietHoursTimeSchema,
});
export type QuietHours = z.infer<typeof QuietHoursSchema>;

/**
 * PUT /me/notification-prefs/quiet-hours body. Either a `{start, end}` pair
 * (set the window) OR `{clear: true}` (disable). Discriminated union so the
 * controller can dispatch without runtime guards.
 */
export const UpdateQuietHoursInputSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('set'), start: QuietHoursTimeSchema, end: QuietHoursTimeSchema }),
  z.object({ mode: z.literal('clear') }),
]);
export type UpdateQuietHoursInput = z.infer<typeof UpdateQuietHoursInputSchema>;

/**
 * GET / setter response. `quietHours` is `null` when disabled, otherwise the
 * {start, end} pair currently configured.
 */
export const QuietHoursResponseSchema = z.object({
  quietHours: QuietHoursSchema.nullable(),
});
export type QuietHoursResponse = z.infer<typeof QuietHoursResponseSchema>;

/**
 * Pure helper: is `nowHHmm` inside the [start, end] quiet window? Handles
 * wrap-around (e.g. 22:00 → 07:00 includes 23:30 AND 03:00). Exported so
 * both the dispatcher (server) and the mobile UI (preview hint) can share
 * the policy.
 *
 *  - `nowHHmm`, `start`, `end` all expected to match QUIET_HOURS_TIME_REGEX.
 *  - When `start === end` returns `false` (caller should treat as disabled).
 *  - Inclusive on `start`, exclusive on `end` — a delivery at exactly the
 *    window-end time fires immediately (matches "the window has ended").
 */
export function isWithinQuietHours(
  nowHHmm: string,
  start: string,
  end: string,
): boolean {
  if (start === end) return false;
  const nowMin = hhmmToMinutes(nowHHmm);
  const startMin = hhmmToMinutes(start);
  const endMin = hhmmToMinutes(end);
  if (Number.isNaN(nowMin) || Number.isNaN(startMin) || Number.isNaN(endMin)) {
    return false;
  }
  if (startMin < endMin) {
    // Non-wrapping window (e.g. 09:00 → 17:00).
    return nowMin >= startMin && nowMin < endMin;
  }
  // Wrapping window (e.g. 22:00 → 07:00): inside if AFTER start OR BEFORE end.
  return nowMin >= startMin || nowMin < endMin;
}

function hhmmToMinutes(hhmm: string): number {
  if (!QUIET_HOURS_TIME_REGEX.test(hhmm)) return Number.NaN;
  const [h, m] = hhmm.split(':');
  return Number.parseInt(h, 10) * 60 + Number.parseInt(m, 10);
}

// ============================================================
// PHASE 47 v1.11 — SNOOZE-INDIV-01: per-notification snooze
// ============================================================

/**
 * Per-notification snooze delay modes. User picks one of three presets from the
 * bell-feed ion-item-sliding actions:
 *
 *   - `'1h'`      — re-dispatch push 1 hour from now (relative).
 *   - `'4h'`      — re-dispatch push 4 hours from now (relative).
 *   - `'morning'` — re-dispatch push at the next 08:00 America/Sao_Paulo.
 *                   If the user is currently before 08:00 SP, this fires today;
 *                   otherwise it fires tomorrow at 08:00 SP.
 *
 * Backend computes `scheduledFor` from the delay mode and inserts ONE
 * NotificationDelivery row (channel=PUSH, status=PENDING). Email is NOT
 * re-dispatched — it was already sent at the original dispatch time and a
 * second email would be spam. The Phase 45 `QuietHoursScheduler.flushDelayed`
 * cron picks up the row once `scheduledFor <= NOW()` and sends the push.
 *
 * Why a closed enum and not raw hours: limits API surface to the three UX
 * actions actually shipped, prevents arbitrary snooze-spam attacks (a user
 * cannot snooze for 30 days), and keeps `scheduledFor` computation server-
 * side (timezone-correct).
 */
export const SnoozeDelayModeSchema = z.enum(['1h', '4h', 'morning']);
export type SnoozeDelayMode = z.infer<typeof SnoozeDelayModeSchema>;

/**
 * POST /me/notifications/:id/snooze body. The `id` is in the path; the body
 * carries only the chosen delay preset.
 */
export const SnoozeNotificationInputSchema = z.object({
  delayMode: SnoozeDelayModeSchema,
});
export type SnoozeNotificationInput = z.infer<
  typeof SnoozeNotificationInputSchema
>;

/**
 * POST /me/notifications/:id/snooze response. Returns the scheduled wakeup
 * time (ISO 8601 UTC) so the mobile UI can render the green confirmation
 * toast ("Lembraremos em Xh" / "Lembraremos amanhã às 08:00").
 */
export const SnoozeNotificationResponseSchema = z.object({
  notificationId: z.string(),
  delayMode: SnoozeDelayModeSchema,
  scheduledFor: z.string(),
});
export type SnoozeNotificationResponse = z.infer<
  typeof SnoozeNotificationResponseSchema
>;

